import { useEffect, useState } from 'react'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

export interface AppConfig {
  /** When true, NFC + e-certificate steps are mocked: one-click publish, no NFC UI, mock verify. */
  nfcMockMode: boolean
}

const DEFAULT_CONFIG: AppConfig = { nfcMockMode: false }

// Module-level cache so the config is fetched once per page load and shared across hooks.
let cached: AppConfig | null = null

/**
 * Reads non-sensitive runtime config from the backend (GET /api/public/config) so the UI can
 * adapt without a rebuild. Currently just whether NFC mock mode is on.
 */
export function useAppConfig(): { config: AppConfig; loading: boolean } {
  const [config, setConfig] = useState<AppConfig>(cached ?? DEFAULT_CONFIG)
  const [loading, setLoading] = useState(!cached)

  useEffect(() => {
    if (cached) return
    let active = true
    fetch(`${API_BASE_URL}/api/public/config`)
      .then(res => (res.ok ? res.json() : DEFAULT_CONFIG))
      .then((data: Partial<AppConfig>) => {
        cached = { nfcMockMode: !!data.nfcMockMode }
        if (active) {
          setConfig(cached)
          setLoading(false)
        }
      })
      .catch(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [])

  return { config, loading }
}

/**
 * Derives the deterministic virtual NFC UID a listing gets in mock mode.
 * MUST stay in sync with the backend MockProvisioningService: the first 14 hex chars of the
 * listing UUID, uppercased (a valid public /v UID).
 */
export function mockVirtualUid(listingId: string): string {
  return listingId.replace(/-/g, '').substring(0, 14).toUpperCase()
}
